import React, { useEffect, useState, useRef } from "react";
import GoogleMapReact from "google-map-react";
import { Select, Input, Row, Col, Form, Spin, TimePicker } from "antd";
import { isEmpty, debounce } from "lodash";
import { getCurrentUserLocation } from "../../../../utils/helpers";
import {
  GOOGLE_MAPS_BOOTSTRAP_URL_KEYS,
  loadGoogleMapsApi,
} from "../../../../utils/googleMaps";

import { DateRangePicker } from "react-date-range";

import moment from "moment";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

const getStartOfToday = () => moment().startOf("day").toDate();

const toDayKey = (date) => moment(date).format("YYYY-MM-DD");

const normalizeEventDateRange = (start, end, minDate = getStartOfToday()) => {
  const clamp = (date) => {
    if (!date || moment(date).isBefore(minDate, "day")) {
      return new Date(minDate);
    }
    return new Date(date);
  };

  const normalizedStart = clamp(start);
  let normalizedEnd = clamp(end);

  if (moment(normalizedEnd).isBefore(normalizedStart, "day")) {
    normalizedEnd = normalizedStart;
  }

  return [normalizedStart, normalizedEnd];
};

const eventDateRangesEqual = (left, right) => {
  if (!left?.length || !right?.length) return false;

  return (
    toDayKey(left[0]) === toDayKey(right[0]) &&
    toDayKey(left[1]) === toDayKey(right[1])
  );
};

const parseStoredEventTime = (time) => {
  if (!time) return null;
  if (moment.isMoment(time)) return time;
  if (typeof time === "string") {
    const parsed = moment(time, ["h:mm a", "HH:mm", moment.ISO_8601], true);
    return parsed.isValid() ? parsed : moment(time);
  }
  return moment(time);
};

const { Option } = Select;

const MapMarker = ({ lat, lng }) => (
  <div>
    <i
      className="fa fa-map-marker"
      style={{
        fontSize: "xxx-large",
        position: "absolute",
        top: "-48px",
        left: "-13.72px",
      }}
    />
  </div>
);

const autoCompleteServiceRef = { current: null };
const geocoderRef = { current: null };
const lastSearchQueryRef = { current: "" };
const setFetchedAddressesRef = { current: null };

const resetGoogleMapServiceRefs = () => {
  autoCompleteServiceRef.current = null;
  geocoderRef.current = null;
};

const initGoogleMapServices = (maps) => {
  if (!maps?.places) return null;

  autoCompleteServiceRef.current = new maps.places.AutocompleteService();
  geocoderRef.current = new maps.Geocoder();

  return {
    autoCompleteService: autoCompleteServiceRef.current,
    geocoder: geocoderRef.current,
  };
};

const searchPlaces = (value, setFetchedAddresses) => {
  lastSearchQueryRef.current = value || "";
  if (setFetchedAddresses) {
    setFetchedAddressesRef.current = setFetchedAddresses;
  }

  const autoCompleteService = autoCompleteServiceRef.current;

  if (!value || !autoCompleteService) {
    return setFetchedAddressesRef.current?.([]);
  }

  autoCompleteService.getPlacePredictions(
    {
      input: value,
      componentRestrictions: { country: "au" },
    },
    (addresses) => {
      const updateFetchedAddresses = setFetchedAddressesRef.current;
      if (!updateFetchedAddresses) return;

      if (!addresses || !addresses.length) {
        return updateFetchedAddresses([]);
      }

      updateFetchedAddresses(
        addresses.map(({ description, place_id }) => ({
          value: description,
          key: place_id,
        })),
      );
    },
  );
};

const handleSearchPlaces = debounce(searchPlaces, 300);

const GEOCODER_WAIT_MS = 10000;

const waitForGeocoder = (timeoutMs = GEOCODER_WAIT_MS, intervalMs = 200) =>
  new Promise((resolve) => {
    if (geocoderRef.current) {
      resolve(geocoderRef.current);
      return;
    }

    const deadline = Date.now() + timeoutMs;
    const intervalId = setInterval(() => {
      if (geocoderRef.current) {
        clearInterval(intervalId);
        resolve(geocoderRef.current);
      } else if (Date.now() >= deadline) {
        clearInterval(intervalId);
        resolve(null);
      }
    }, intervalMs);
  });

const geocodeByPlaceId = (placeId) =>
  new Promise((resolve) => {
    if (!geocoderRef.current) {
      resolve(null);
      return;
    }

    geocoderRef.current.geocode({ placeId }, (results, status) => {
      const ok = !status || status === "OK";
      if (!ok || !results?.length) {
        resolve(null);
        return;
      }

      const { geometry } = results[0];
      resolve({
        lat: geometry.location.lat(),
        lng: geometry.location.lng(),
      });
    });
  });

const geocodeByCoordinates = (lat, lng) =>
  new Promise((resolve) => {
    if (!geocoderRef.current) {
      resolve(null);
      return;
    }

    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      const ok = !status || status === "OK";
      if (!ok || !results?.length) {
        resolve(null);
        return;
      }

      const streetAddress = results.find((r) => r.types.includes("route"));
      const fallback = results[0];

      resolve({
        key: streetAddress?.place_id || fallback.place_id || "",
        value:
          streetAddress?.formatted_address ||
          fallback.formatted_address ||
          `${lat}, ${lng}`,
      });
    });
  });

const coordsToAddressLabel = (lat, lng) => ({
  key: "",
  value: `${lat}, ${lng}`,
});

const EventCreation5 = (props) => {
  const [form] = Form.useForm();

  const [autoCompleteService, setAutoCompleteService] = useState(null);

  const [geocoder, setGeocoder] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      lastSearchQueryRef.current = "";
      setFetchedAddressesRef.current = null;
      resetGoogleMapServiceRefs();
      handleSearchPlaces.cancel();
    };
  }, []);

  const onFinishFailed = (error) => {};

  const getLocationInfoForSelectedAddress = async (
    addressKey,
    setCurrentLocationCoordinates,
    setCurrentLocationAddress,
    setLoading,
  ) => {
    if (!addressKey) return;

    const [placeId, addressString] = addressKey.split("&&&$$$***");
    setLoading(true);

    if (isMountedRef.current) {
      setCurrentLocationAddress({
        key: placeId,
        value: addressString,
      });
    }

    try {
      await waitForGeocoder();
      if (!isMountedRef.current) return;

      const coords = await geocodeByPlaceId(placeId);
      if (!isMountedRef.current || !coords) return;

      setCurrentLocationCoordinates(coords);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const onClear = async (
    setFetchedAddresses,
    setLoading,
    setCurrentLocationCoordinates,
    setCurrentLocationAddress,
  ) => {
    setFetchedAddresses([]);
    setCurrentLocationAddress({ key: "", value: "" });
    setLoading(true);

    try {
      const { lat, lng } = await getCurrentUserLocation();
      if (!isMountedRef.current) return;

      setCurrentLocationCoordinates({ lat, lng });

      await waitForGeocoder();
      if (!isMountedRef.current) return;

      const address = await geocodeByCoordinates(lat, lng);
      if (!isMountedRef.current) return;

      setCurrentLocationAddress(address || coordsToAddressLabel(lat, lng));
    } catch {
      if (!isMountedRef.current) return;

      const lat = -33.8688;
      const lng = 151.2093;
      setCurrentLocationCoordinates({ lat, lng });
      setCurrentLocationAddress({ key: "", value: "Sydney, AU" });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const {
    inEditMode,
    setSelectedStep,
    selectedStep,
    selectedEventInformation,
    setSelectedEventInformation,
  } = props;
  const { eventName, eventDate } = selectedEventInformation;
  const today = getStartOfToday();
  const todayKey = moment(today).format("YYYY-MM-DD");
  const [calendarStartDate, calendarEndDate] = normalizeEventDateRange(
    eventDate?.[0],
    eventDate?.[1],
    today,
  );
  const storedEventStartTime = parseStoredEventTime(
    selectedEventInformation?.eventStartTime,
  );
  const storedEventEndTime = parseStoredEventTime(
    selectedEventInformation?.eventEndTime,
  );

  const [error, setError] = useState("");

  const [loading, setLoading] = useState(false);
  const [fetchedAddresses, setFetchedAddresses] = useState([]);
  setFetchedAddressesRef.current = setFetchedAddresses;

  const [currentLocationCoordinates, setCurrentLocationCoordinates] = useState(
    selectedEventInformation &&
      selectedEventInformation.lat &&
      selectedEventInformation.lng
      ? {
          lat: selectedEventInformation.lat,
          lng: selectedEventInformation.lng,
        }
      : {
          lat: -33.8688, // Sydney fallback
          lng: 151.2093,
        },
  );
  const [currentLocationAddress, setCurrentLocationAddress] = useState(
    selectedEventInformation &&
      selectedEventInformation.key &&
      selectedEventInformation.value
      ? {
          key: selectedEventInformation.key,
          value: selectedEventInformation.value,
        }
      : {},
  );

  useEffect(() => {
    if (!isMountedRef.current) return;
    if (
      !autoCompleteServiceRef.current ||
      !lastSearchQueryRef.current ||
      !setFetchedAddressesRef.current
    ) {
      return;
    }

    searchPlaces(lastSearchQueryRef.current, setFetchedAddressesRef.current);
  }, [autoCompleteService]);

  const needsInitialAddress =
    !selectedEventInformation?.key || !selectedEventInformation?.value;

  useEffect(() => {
    const normalizedEventDate = normalizeEventDateRange(
      eventDate?.[0],
      eventDate?.[1],
      moment(todayKey, "YYYY-MM-DD").startOf("day").toDate(),
    );

    if (!eventDate?.length || !eventDateRangesEqual(eventDate, normalizedEventDate)) {
      setSelectedEventInformation((prev) => ({
        ...prev,
        eventDate: normalizedEventDate,
      }));
    }
  }, [eventDate, todayKey, setSelectedEventInformation]);

  useEffect(() => {
    setError("");

    if (!needsInitialAddress) return;

    const resolveInitialLocation = async () => {
      if (!isMountedRef.current) return;

      setLoading(true);
      try {
        const { lat, lng } = await getCurrentUserLocation();
        if (!isMountedRef.current) return;

        setCurrentLocationCoordinates({ lat, lng });

        await waitForGeocoder();
        if (!isMountedRef.current) return;

        const address = await geocodeByCoordinates(lat, lng);
        if (!isMountedRef.current) return;

        setCurrentLocationAddress(address || coordsToAddressLabel(lat, lng));
      } catch {
        if (!isMountedRef.current) return;

        setCurrentLocationCoordinates({ lat: -33.8688, lng: 151.2093 });
        setCurrentLocationAddress({ key: "", value: "Sydney, AU" });
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    resolveInitialLocation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyGoogleMapServices = (maps) => {
    const services = initGoogleMapServices(maps);
    if (!services) return;

    setAutoCompleteService(services.autoCompleteService);
    setGeocoder(services.geocoder);
  };

  useEffect(() => {
    if (geocoder) return;

    let cancelled = false;

    loadGoogleMapsApi()
      .then((maps) => {
        if (!cancelled && isMountedRef.current) {
          applyGoogleMapServices(maps);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [geocoder]);

  const handleGoogleApiLoaded = ({ maps }) => {
    applyGoogleMapServices(maps);
  };

  const onFinish = ({
    values,
    currentLocationAddress,
    currentLocationCoordinates,
    setSelectedStep,
    stepNumber,
    setSelectedEventInformation,
    selectedEventInformation,
  }) => {
    setError("");
    const result =
      moment(values.eventStartTime?._d).format("h:mm") >=
      moment(values.eventEndTime?._d).format("h:mm");

    const [startDate, endDate] = normalizeEventDateRange(
      selectedEventInformation.eventDate?.[0],
      selectedEventInformation.eventDate?.[1],
    );
    const resultDate = moment(startDate).isSame(endDate, "day");

    if (result && resultDate) {
      setError("Please select a valid time slot!");
    } else {
      setSelectedEventInformation({
        ...selectedEventInformation,
        ...currentLocationAddress,
        ...currentLocationCoordinates,
        ...values,
        eventDate: [startDate, endDate],
      });
      setSelectedStep(++stepNumber);
    }
  };

  let stepNumber = selectedStep;
  const options = fetchedAddresses.map(({ value, key }) => (
    <Option key={`${key}&&&$$$***${value}`}>{value}</Option>
  ));
  return (
    <div>
      <Spin spinning={false}>
        {inEditMode && (
          <div
            style={{ backgroundColor: inEditMode ? "white" : "grey" }}
            className="custom-list create-event-top-nav"
          >
            <ul>
              <li>
                <i className="fa fa-edit mr-2"></i>
                <button
                  disabled={!inEditMode}
                  onClick={() => setSelectedStep(1)}
                  style={{ background: "none", border: "0px solid" }}
                >
                  Intro
                </button>
              </li>
              <li className="active">
                <i className="fa fa-edit mr-2"></i>
                <button
                  disabled
                  style={{ background: "none", border: "0px solid" }}
                >
                  Basic Information
                </button>
              </li>
              <li>
                <i className="fa fa-edit mr-2"></i>
                <button
                  disabled={!inEditMode}
                  onClick={() => setSelectedStep(3)}
                  style={{ background: "none", border: "0px solid" }}
                >
                  Event Creation
                </button>
              </li>
              <li>
                <i className="fa fa-edit mr-2"></i>
                <button
                  disabled={!inEditMode}
                  onClick={() => setSelectedStep(4)}
                  style={{ background: "none", border: "0px solid" }}
                >
                  Publish Event
                </button>
              </li>
            </ul>
          </div>
        )}
        <section className="intro">
          <div className="create-event-2-wrap">
            <div className="container text-center">
              <Form
                name="basic"
                form={form}
                initialValues={{
                  eventName,
                  eventStartTime: storedEventStartTime,
                  eventEndTime: storedEventEndTime,
                  eventDate,
                }}
                onFinish={(values) =>
                  onFinish({
                    form,
                    values,
                    props,
                    setSelectedStep,
                    stepNumber,
                    setSelectedEventInformation,
                    selectedEventInformation,
                    currentLocationAddress,
                    currentLocationCoordinates,
                  })
                }
                onFinishFailed={onFinishFailed}
              >
                <div className="create-event-2-box">
                  <span>Event name</span>

                  <Form.Item
                    name="eventName"
                    className="emial-error"
                    rules={[
                      {
                        required: true,
                        message: "Enter a Event name",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter a Event name"
                      style={{ width: "90%" }}
                    />
                  </Form.Item>
                </div>

                <Row style={{ marginTop: 10 }} gutter={15}>
                  <Col span={9} className="event-map-wrap">
                    <div className="event-map-wrap-inner">
                      <div className="event-map-text">
                        <label>Please select location</label>
                      </div>
                      <Select
                        style={{ width: "100%" }}
                        value={
                          currentLocationAddress && currentLocationAddress.value
                        }
                        showSearch
                        allowClear
                        placeholder="Enter Address"
                        defaultActiveFirstOption={false}
                        filterOption={false}
                        onClear={() =>
                          onClear(
                            setFetchedAddresses,
                            setLoading,
                            setCurrentLocationCoordinates,
                            setCurrentLocationAddress,
                          )
                        }
                        onSearch={(value) =>
                          handleSearchPlaces(value, setFetchedAddresses)
                        }
                        onChange={(address) =>
                          getLocationInfoForSelectedAddress(
                            address,
                            setCurrentLocationCoordinates,
                            setCurrentLocationAddress,
                            setLoading,
                          )
                        }
                        notFoundContent={null}
                      >
                        {options}
                      </Select>
                      <Spin spinning={loading}>
                        <div style={{ height: "350px" }}>
                          {!isEmpty(currentLocationCoordinates) && (
                            <GoogleMapReact
                              bootstrapURLKeys={GOOGLE_MAPS_BOOTSTRAP_URL_KEYS}
                              center={currentLocationCoordinates}
                              defaultZoom={15}
                              yesIWantToUseGoogleMapApiInternals
                              onGoogleApiLoaded={handleGoogleApiLoaded}
                            >
                              <MapMarker
                                lat={currentLocationCoordinates.lat}
                                lng={currentLocationCoordinates.lng}
                              />
                            </GoogleMapReact>
                          )}
                        </div>
                      </Spin>
                    </div>
                  </Col>
                  <Col span={15} className="event-calender-time-wrap">
                    <div className="create-event-2-box create-event-2-time">
                      <span>Event Date & Timing</span>
                      <br />
                      <br />
                      <br />
                      <br />
                      <DateRangePicker
                        className="event-date-calender"
                        ranges={[
                          {
                            startDate: calendarStartDate,
                            endDate: calendarEndDate,
                            key: "selection",
                          },
                        ]}
                        months={2}
                        direction="horizontal"
                        minDate={today}
                        staticRanges={[]}
                        inputRanges={[]}
                        showSelectionPreview={false}
                        onChange={({ selection }) => {
                          const { startDate, endDate } = selection;
                          if (!startDate || !endDate) return;

                          const [normalizedStart, normalizedEnd] =
                            normalizeEventDateRange(startDate, endDate, today);

                          setSelectedEventInformation((prev) => ({
                            ...prev,
                            eventDate: [normalizedStart, normalizedEnd],
                          }));
                        }}
                      />
                      <div className="event-time-wrap">
                        <Row gutter={24}>
                          <Col span={12}>
                            <Form.Item
                              name="eventStartTime"
                              label="Start Time"
                              rules={[
                                {
                                  required: true,
                                  message: "Select an Event start time",
                                },
                              ]}
                            >
                              <TimePicker
                                use12Hours
                                format="h:mm a"
                                size="small"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="eventEndTime"
                              label="End Time"
                              rules={[
                                {
                                  required: true,
                                  message: "Select an Event end time",
                                },
                              ]}
                            >
                              <TimePicker
                                use12Hours
                                format="h:mm a"
                                size="small"
                              />
                            </Form.Item>
                          </Col>
                          {error && (
                            <div style={{ marginLeft: "30%" }}>
                              <p style={{ color: "red", marginTop: "10px" }}>
                                {error}
                              </p>
                            </div>
                          )}
                        </Row>
                      </div>
                    </div>
                  </Col>
                </Row>
                <div className="flex">
                  <button
                    onClick={() => setSelectedStep(1)}
                    className="btn btn-outline-dark text-center d-flex my-5 mx-auto"
                  >
                    Previous
                  </button>
                  <button
                    type="submit"
                    className="btn btn-outline-dark text-center d-flex my-5 mx-auto"
                  >
                    NEXT
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </section>
      </Spin>
    </div>
  );
};

export default EventCreation5;
